import React, { useState } from 'react';
import debounce from 'lodash/debounce';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Archive, RotateCcw, Trash2, Upload } from 'lucide-react';
import Loader from 'src/shared/components/loader/Loader';
import useAuthService from 'src/shared/hooks/useAuthService';
import { SecondaryButton } from 'src/shared/components/button/Button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from 'src/shared/components/ui/sheet';
import Autocomplete from 'src/shared/components/autocomplete/Autocomplete';
import SnackbarAlert from 'src/shared/components/snackbar-alert/SnackbarAlert';
import { ControlledConfirmDialog } from 'src/shared/components/confirm/ControlledConfirmDialog';
import styles from './SiteDetail.module.scss';
import TimelineSection from './components/timeline-section/TimelineSection';
import VersionDrawer from './components/version-drawer/VersionDrawer';
import GeographyVersionContent from './components/version-drawer/GeographyVersionContent';
import ActivityVersionContent from './components/version-drawer/ActivityVersionContent';
import SiteForm from '../../components/site-form/SiteForm';
import {
    useGetSiteQuery,
    useUpdateSiteMutation,
    useDeleteSiteMutation,
    usePurgeSiteMutation,
    useUpdateSiteValidityMutation,
    useListGeographiesQuery,
    useListActivitiesQuery,
    useCreateGeographyMutation,
    useConvertGeoJSONToGeographyMutation,
    useUpdateGeographyGeometryMutation,
    useRemoveGeographyVersionMutation,
    usePatchGeographyValidityMutation,
    useSetActivityMutation,
    useListIsicCodesQuery,
    useRemoveActivityVersionMutation,
    usePatchActivityValidityMutation,
} from '../../state/api/organisationEngineSlice';
import { normaliseGeoJsonFile } from '../../helpers/normaliseGeoJsonFile';

type SiteDetailProps = {
    currentCustomerId: string;
};

const statusLabels: Record<string, string> = {
    draft: 'Draft',
    active: 'Active',
    archived: 'Archived',
};

const statusColors: Record<string, string> = {
    draft: 'bg-orange-0 text-orange-100',
    active: 'bg-green-0 text-green-100',
    archived: 'bg-grey-20 text-grey-70',
};

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className={styles.infoRow}>
        <span className={styles.infoLabel}>{label}</span>
        <span className={styles.infoValue}>{value ?? '—'}</span>
    </div>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        <div className={styles.sectionContent}>{children}</div>
    </div>
);

const extractErrorMessage = (err: unknown): string => {
    if (err && typeof err === 'object') {
        const e = err as any;
        if (e.data?.error) return e.data.error;
        if (e.message) return e.message;
        if (e.error) return e.error;
    }
    return 'Something went wrong. Please try again.';
};

type SnackbarState = { open: boolean; message: string; severity: 'error' | 'success' };

const SiteDetail = ({ currentCustomerId }: SiteDetailProps) => {
    const { siteId } = useParams<{ siteId: string }>();
    const navigate = useNavigate();
    const [showEditForm, setShowEditForm] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'error' });

    const showError = (err: unknown) => setSnackbar({ open: true, message: extractErrorMessage(err), severity: 'error' });
    const showSuccess = (msg: string) => setSnackbar({ open: true, message: msg, severity: 'success' });

    const [showArchiveDialog, setShowArchiveDialog] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [showPurgeDialog, setShowPurgeDialog] = useState(false);
    const [purgeConfirmText, setPurgeConfirmText] = useState('');
    const [isPurging, setIsPurging] = useState(false);
    const { isCurrentUserSuperAdmin } = useAuthService();
    const [isicSearchInput, setIsicSearchInput] = useState('');
    const [debouncedIsicSearch, setDebouncedIsicSearch] = useState('');

    const [versionDrawer, setVersionDrawer] = useState<
        | { type: 'geography'; index: number }
        | { type: 'activity'; index: number }
        | { type: 'addGeography' }
        | { type: 'addActivity' }
        | null
    >(null);

    const { data: site, isLoading, error } = useGetSiteQuery({ bcId: currentCustomerId, siteId: siteId! }, { skip: !siteId });
    const { data: geographiesData } = useListGeographiesQuery({ bcId: currentCustomerId, siteId: siteId! }, { skip: !siteId });
    const { data: activitiesData } = useListActivitiesQuery({ bcId: currentCustomerId, siteId: siteId! }, { skip: !siteId });
    const debouncedSetIsicSearch = React.useCallback(
        debounce((value: string) => {
            setDebouncedIsicSearch(value);
        }, 300),
        []
    );

    React.useEffect(() => {
        return () => {
            debouncedSetIsicSearch.cancel();
        };
    }, [debouncedSetIsicSearch]);

    const { data: isicCodesData } = useListIsicCodesQuery({ search: debouncedIsicSearch });

    const allGeographies = geographiesData?.items ?? [];
    const allActivities = activitiesData?.items ?? [];
    const [updateSite] = useUpdateSiteMutation();
    const [deleteSite] = useDeleteSiteMutation();
    const [purgeSite] = usePurgeSiteMutation();
    const [updateSiteValidity] = useUpdateSiteValidityMutation();
    const [createGeography] = useCreateGeographyMutation();
    const [convertGeoJSONToGeography] = useConvertGeoJSONToGeographyMutation();
    const [updateGeographyGeometry] = useUpdateGeographyGeometryMutation();
    const [removeGeographyVersion] = useRemoveGeographyVersionMutation();
    const [patchGeographyValidity] = usePatchGeographyValidityMutation();
    const [_setActivity] = useSetActivityMutation();
    const [removeActivityVersion] = useRemoveActivityVersionMutation();
    const [patchActivityValidity] = usePatchActivityValidityMutation();

    const geoFileInputRef = React.useRef<HTMLInputElement>(null);
    type PendingUpload = { kind: 'add' } | { kind: 'change'; geographyId: string };
    // Ref (not state): the file input's click triggers the OS file chooser synchronously
    // on the same event handler, so we need the mode-setter to be committed before then.
    // A ref updates synchronously; a useState update wouldn't be visible until after
    // React commits, which races with Playwright's filechooser event.
    const pendingUploadRef = React.useRef<PendingUpload | null>(null);

    const openAddGeographyUpload = () => {
        pendingUploadRef.current = { kind: 'add' };
        geoFileInputRef.current?.click();
    };

    const openChangeGeographyUpload = (geographyId: string) => {
        pendingUploadRef.current = { kind: 'change', geographyId };
        geoFileInputRef.current?.click();
    };

    const handleGeoFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        const mode = pendingUploadRef.current;
        if (!file || !siteId || !mode) return;
        if (geoFileInputRef.current) geoFileInputRef.current.value = '';
        pendingUploadRef.current = null;

        try {
            const canonical = await normaliseGeoJsonFile(
                { convertGeoJSONToGeography: input => convertGeoJSONToGeography(input).unwrap() },
                { file }
            );
            if (mode.kind === 'add') {
                await createGeography({ bcId: currentCustomerId, siteId, body: canonical }).unwrap();
                setVersionDrawer(null);
                showSuccess('New geography version added.');
            } else {
                await updateGeographyGeometry({
                    bcId: currentCustomerId,
                    siteId,
                    geographyId: mode.geographyId,
                    body: canonical,
                }).unwrap();
                setVersionDrawer(null);
                showSuccess('Geography corrected.');
            }
        } catch (err) {
            showError(err);
        }
    };

    const handlePatchGeographyValidity = async (geographyId: string, validFrom: string, validTo: string | null) => {
        if (!siteId) return;
        try {
            await patchGeographyValidity({
                bcId: currentCustomerId,
                siteId,
                geographyId,
                body: { valid_from: validFrom, valid_to: validTo },
            }).unwrap();
            showSuccess('Geography validity updated.');
        } catch (err) {
            showError(err);
        }
    };

    const handlePatchActivityValidity = async (activityId: string, validFrom: string, validTo: string | null) => {
        if (!siteId) return;
        try {
            await patchActivityValidity({
                bcId: currentCustomerId,
                siteId,
                activityId,
                body: { valid_from: validFrom, valid_to: validTo },
            }).unwrap();
            showSuccess('Activity validity updated.');
        } catch (err) {
            showError(err);
        }
    };

    const handleArchiveGeography = async () => {
        const geo = versionDrawer?.type === 'geography' ? allGeographies[versionDrawer.index] : null;
        if (!siteId || !geo) return;
        try {
            await patchGeographyValidity({
                bcId: currentCustomerId,
                siteId,
                geographyId: geo.id,
                body: { valid_from: geo.properties.valid_from, valid_to: new Date().toISOString() },
            }).unwrap();
            setVersionDrawer(null);
            showSuccess('Geography archived.');
        } catch (err) {
            showError(err);
        }
    };

    const handleRemoveGeography = async () => {
        const geo = versionDrawer?.type === 'geography' ? allGeographies[versionDrawer.index] : null;
        if (!siteId || !geo) return;
        try {
            await removeGeographyVersion({ bcId: currentCustomerId, siteId, geographyId: geo.id }).unwrap();
            setVersionDrawer(null);
            showSuccess('Geography removed.');
        } catch (err) {
            showError(err);
        }
    };

    const handleArchiveActivity = async () => {
        const act = versionDrawer?.type === 'activity' ? allActivities[versionDrawer.index] : null;
        if (!siteId || !act) return;
        try {
            await patchActivityValidity({
                bcId: currentCustomerId,
                siteId,
                activityId: act.id,
                body: { valid_from: act.valid_from, valid_to: new Date().toISOString() },
            }).unwrap();
            setVersionDrawer(null);
            showSuccess('Activity archived.');
        } catch (err) {
            showError(err);
        }
    };

    const handleRemoveActivity = async () => {
        const act = versionDrawer?.type === 'activity' ? allActivities[versionDrawer.index] : null;
        if (!siteId || !act) return;
        try {
            await removeActivityVersion({ bcId: currentCustomerId, siteId, activityId: act.id }).unwrap();
            setVersionDrawer(null);
            showSuccess('Activity removed.');
        } catch (err) {
            showError(err);
        }
    };

    const isicOptions = React.useMemo(
        () =>
            (isicCodesData?.items ?? []).map(entry => ({
                value: entry.code,
                label: `${entry.code} — ${entry.description}`,
                labelSubText: entry.level,
            })),
        [isicCodesData]
    );

    const handleIsicInputChange = (value: string) => {
        setIsicSearchInput(value);
        debouncedSetIsicSearch(value);
    };

    const handleSetActivity = async (code: string) => {
        if (!siteId) return;
        try {
            await _setActivity({ bcId: currentCustomerId, siteId, body: { isic_code: code } }).unwrap();
            setVersionDrawer(null);
            showSuccess('Activity set.');
        } catch (err) {
            showError(err);
        }
    };

    const handleArchiveSite = async () => {
        if (!siteId) return;
        setIsArchiving(true);
        try {
            await deleteSite({ bcId: currentCustomerId, siteId }).unwrap();
            setShowArchiveDialog(false);
            navigate(-1);
        } catch (err) {
            setIsArchiving(false);
            showError(err);
        }
    };

    const handlePurgeSite = async () => {
        if (!siteId) return;
        setIsPurging(true);
        try {
            await purgeSite({ bcId: currentCustomerId, siteId }).unwrap();
            setShowPurgeDialog(false);
            setPurgeConfirmText('');
            navigate(-1);
        } catch (err) {
            setIsPurging(false);
            showError(err);
        }
    };

    const handleUpdateSite = async (values: {
        name: string;
        business_central_id: string | undefined;
        valid_from?: string;
        valid_to?: string | null;
    }) => {
        if (!siteId) return;
        setIsUpdating(true);
        try {
            await updateSite({
                bcId: currentCustomerId,
                siteId,
                body: { name: values.name, business_central_id: values.business_central_id ?? null },
            }).unwrap();
            if (values.valid_from) {
                await updateSiteValidity({
                    bcId: currentCustomerId,
                    siteId,
                    body: { valid_from: values.valid_from, valid_to: values.valid_to ?? null },
                }).unwrap();
            }
            setShowEditForm(false);
            showSuccess('Site updated.');
        } catch (err) {
            showError(err);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleRestoreSite = async () => {
        if (!siteId || !site) return;
        setIsRestoring(true);
        try {
            await updateSiteValidity({
                bcId: currentCustomerId,
                siteId,
                body: { valid_from: site.valid_from, valid_to: null },
            }).unwrap();
            showSuccess('Site restored.');
        } catch (err) {
            showError(err);
        } finally {
            setIsRestoring(false);
        }
    };

    if (isLoading || !siteId) {
        return (
            <div className={styles.loadingContainer} data-testid='site-detail-loading'>
                <Loader />
            </div>
        );
    }

    if (error || !site) {
        return (
            <div className='flex flex-col items-center justify-center py-24 gap-4'>
                <p className='text-base text-grey-70'>Failed to load site details. Please try again.</p>
                <button className={styles.backButton} onClick={() => navigate(-1)} data-testid='back-to-sites'>
                    <ArrowLeft className='w-4 h-4' /> Back to sites
                </button>
            </div>
        );
    }

    return (
        <div className={styles.container} data-testid='site-detail'>
            <div className={styles.header}>
                <button className={styles.backButton} onClick={() => navigate(-1)} data-testid='back-to-sites'>
                    <ArrowLeft className='w-4 h-4' /> Back to sites
                </button>
                <div className={styles.headerActions}>
                    <SecondaryButton onClick={() => setShowEditForm(true)} data-testid='edit-site-button'>
                        <Pencil className='w-4 h-4' /> Edit
                    </SecondaryButton>
                    {site.status === 'archived' ? (
                        <>
                            <SecondaryButton onClick={handleRestoreSite} disabled={isRestoring} data-testid='restore-site-button'>
                                <RotateCcw className='w-4 h-4' /> {isRestoring ? 'Restoring...' : 'Restore'}
                            </SecondaryButton>
                            {isCurrentUserSuperAdmin && (
                                <SecondaryButton onClick={() => setShowPurgeDialog(true)} data-testid='purge-site-button'>
                                    <Trash2 className='w-4 h-4' /> Purge
                                </SecondaryButton>
                            )}
                        </>
                    ) : (
                        <SecondaryButton onClick={() => setShowArchiveDialog(true)} data-testid='archive-site-button'>
                            <Archive className='w-4 h-4' /> Archive
                        </SecondaryButton>
                    )}
                </div>
            </div>

            <ControlledConfirmDialog
                open={showArchiveDialog}
                onOpenChange={setShowArchiveDialog}
                variant='confirm'
                title='Archive Site'
                description={`Are you sure you want to archive "${site.name}"? This will close the site's validity period.`}
                confirmLabel='Archive'
                onConfirm={handleArchiveSite}
                isConfirming={isArchiving}
            />

            <ControlledConfirmDialog
                open={showPurgeDialog}
                onOpenChange={open => {
                    setShowPurgeDialog(open);
                    if (!open) setPurgeConfirmText('');
                }}
                variant='confirm'
                title='Permanently delete site'
                description={
                    <div className='flex flex-col gap-3'>
                        <p>
                            This will permanently delete <strong>{site.name}</strong> and all its geographies, activities, and risk data.
                            This action cannot be undone.
                        </p>
                        <label className='flex flex-col gap-1 text-left'>
                            <span>Type the site name to confirm:</span>
                            <input
                                type='text'
                                value={purgeConfirmText}
                                onChange={e => setPurgeConfirmText(e.target.value)}
                                className='border border-grey-30 rounded px-2 py-1 text-sm'
                                data-testid='purge-confirm-input'
                                autoFocus
                            />
                        </label>
                    </div>
                }
                confirmLabel='Delete permanently'
                confirmDisabled={purgeConfirmText !== site.name}
                onConfirm={handlePurgeSite}
                isConfirming={isPurging}
            />

            <Sheet open={showEditForm} onOpenChange={setShowEditForm}>
                <SheetContent side='right' className='w-[400px] sm:max-w-[400px]'>
                    <SheetHeader>
                        <SheetTitle>Edit Site</SheetTitle>
                        <SheetDescription>Update site name and project code.</SheetDescription>
                    </SheetHeader>
                    <div className='mt-6'>
                        <SiteForm
                            onSubmit={handleUpdateSite}
                            onCancel={() => setShowEditForm(false)}
                            isSubmitting={isUpdating}
                            submitLabel='Save Changes'
                            showValidity
                            initialValues={{
                                name: site.name,
                                business_central_id: site.business_central_id,
                                valid_from: site.valid_from,
                                valid_to: site.valid_to,
                            }}
                        />
                    </div>
                </SheetContent>
            </Sheet>

            <div className={styles.titleRow}>
                <h2 className={styles.siteName}>{site.name}</h2>
                <span className={`${styles.statusBadge} ${statusColors[site.status]}`} data-testid='site-status-badge'>
                    {statusLabels[site.status]}
                </span>
            </div>

            <Section title='Timeline'>
                <TimelineSection
                    site={site}
                    geographies={allGeographies}
                    activities={allActivities}
                    onBarClick={(rowLabel, segIdx) => {
                        if (rowLabel === 'Site') {
                            setShowEditForm(true);
                        } else if (rowLabel === 'Location' && allGeographies[segIdx]) {
                            setVersionDrawer({ type: 'geography', index: segIdx });
                        } else if (rowLabel === 'Activity' && allActivities[segIdx]) {
                            setVersionDrawer({ type: 'activity', index: segIdx });
                        }
                    }}
                    onAddClick={rowLabel => {
                        if (rowLabel === 'Location') {
                            setVersionDrawer({ type: 'addGeography' });
                        } else if (rowLabel === 'Activity') {
                            setVersionDrawer({ type: 'addActivity' });
                        }
                    }}
                />
            </Section>

            <VersionDrawer
                open={versionDrawer?.type === 'geography'}
                onOpenChange={open => {
                    if (!open) setVersionDrawer(null);
                }}
                title='Location (Geography)'
                description='Details of this geography version'
            >
                {versionDrawer?.type === 'geography' && allGeographies[versionDrawer.index] && (
                    <GeographyVersionContent
                        geography={allGeographies[versionDrawer.index]}
                        onChange={() => openChangeGeographyUpload(allGeographies[versionDrawer.index].id)}
                        onUpdateValidity={handlePatchGeographyValidity}
                        onArchive={handleArchiveGeography}
                        onRemove={handleRemoveGeography}
                    />
                )}
            </VersionDrawer>

            <VersionDrawer
                open={versionDrawer?.type === 'activity'}
                onOpenChange={open => {
                    if (!open) setVersionDrawer(null);
                }}
                title='Activity Version'
                description='Details of this activity version'
            >
                {versionDrawer?.type === 'activity' && allActivities[versionDrawer.index] && (
                    <ActivityVersionContent
                        activity={allActivities[versionDrawer.index]}
                        onChange={() => setVersionDrawer({ type: 'addActivity' })}
                        onUpdateValidity={handlePatchActivityValidity}
                        onArchive={handleArchiveActivity}
                        onRemove={handleRemoveActivity}
                    />
                )}
            </VersionDrawer>

            <VersionDrawer
                open={versionDrawer?.type === 'addGeography'}
                onOpenChange={open => {
                    if (!open) setVersionDrawer(null);
                }}
                title='Add Location (Geography)'
                description='Upload a GeoJSON file to set the site geography.'
            >
                <div className='flex flex-col gap-3'>
                    <SecondaryButton onClick={openAddGeographyUpload} data-testid='upload-geojson-drawer-button'>
                        <Upload className='w-4 h-4' /> Select GeoJSON file
                    </SecondaryButton>
                    <p className='text-xs text-grey-50'>
                        Accepts .geojson or .json files containing a GeoJSON Feature or Geometry (Polygon, MultiPolygon, or Point).
                    </p>
                </div>
            </VersionDrawer>

            <VersionDrawer
                open={versionDrawer?.type === 'addActivity'}
                onOpenChange={open => {
                    if (!open) {
                        setVersionDrawer(null);
                        setIsicSearchInput('');
                        setDebouncedIsicSearch('');
                    }
                }}
                title='Set Activity'
                description='Select an ISIC code to set as the site activity.'
            >
                <Autocomplete
                    options={isicOptions}
                    value=''
                    inputValue={isicSearchInput}
                    onInputChange={handleIsicInputChange}
                    onChange={val => {
                        if (val) {
                            handleSetActivity(val);
                            setIsicSearchInput('');
                            setDebouncedIsicSearch('');
                        }
                    }}
                    updateInputOnSelect={false}
                    disableClientFilter={true}
                    placeholder='Search ISIC codes...'
                />
            </VersionDrawer>

            {/* Hidden file input — always in DOM for both drawer and version Change button */}
            <input ref={geoFileInputRef} type='file' accept='.geojson,.json' className='hidden' onChange={handleGeoFileSelected} />

            <Section title='Site Details'>
                <InfoRow label='Name' value={site.name} />
                <InfoRow label='Project Code' value={site.business_central_id} />
                <InfoRow label='Status' value={statusLabels[site.status]} />
                <InfoRow label='Valid from' value={new Date(site.valid_from).toLocaleDateString()} />
                <InfoRow label='Valid to' value={site.valid_to ? new Date(site.valid_to).toLocaleDateString() : '—'} />
                <InfoRow label='Created' value={new Date(site.created_at).toLocaleDateString()} />
            </Section>

            <SnackbarAlert
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                autoHideDuration={6000}
                dataTestId='site-detail-snackbar'
            />
        </div>
    );
};

export default SiteDetail;
